import React from "react";
import { palette } from "@/lib/api";

// Bubba — the Pace red panda companion (web SVG, mood-reactive + cosmetics).
const RUST = "#C25A2E", RUST_DARK = "#9E4522", CREAM = "#F7EEE1", NOSE = "#2B211C", EYE = "#241A15";

export function Bubba({ mood = "Happy", cosmetic = "default", size = 150 }) {
  const sleepy = mood === "Sleepy";
  const hungry = mood === "Hungry";
  const proud = mood === "Proud";
  const eyeH = sleepy ? 2 : hungry ? 13 : 10;
  const bounce = mood === "Cheering" || mood === "Proud";

  return (
    <svg width={size} height={size} viewBox="0 0 200 200"
      className={bounce ? "animate-bounce" : ""} style={{ animationDuration: "2s" }}>
      {/* ears */}
      <circle cx="50" cy="48" r="27" fill={RUST_DARK} />
      <circle cx="150" cy="48" r="27" fill={RUST_DARK} />
      <circle cx="50" cy="52" r="15" fill={CREAM} />
      <circle cx="150" cy="52" r="15" fill={CREAM} />
      {/* head */}
      <ellipse cx="100" cy="108" rx="64" ry="58" fill={RUST} />
      {/* white face patch */}
      <path d="M100 74 Q154 78 154 120 Q154 160 100 166 Q46 160 46 120 Q46 78 100 74 Z" fill={CREAM} />
      {/* rust forehead peak (red panda marking) */}
      <path d="M100 74 Q84 100 100 114 Q116 100 100 74 Z" fill={RUST} />
      {/* eyes */}
      {sleepy ? (
        <>
          <path d="M64 108 Q76 114 88 108" stroke={EYE} strokeWidth={4} fill="none" strokeLinecap="round" />
          <path d="M112 108 Q124 114 136 108" stroke={EYE} strokeWidth={4} fill="none" strokeLinecap="round" />
        </>
      ) : (
        <>
          <ellipse cx="76" cy="106" rx="8" ry={eyeH / 2 + 4} fill={EYE} />
          <ellipse cx="124" cy="106" rx="8" ry={eyeH / 2 + 4} fill={EYE} />
          <circle cx="79" cy="103" r="2.6" fill="#fff" />
          <circle cx="127" cy="103" r="2.6" fill="#fff" />
        </>
      )}
      {/* tear-track stripes (red panda signature) */}
      <path d="M70 116 Q62 136 80 152" stroke={RUST} strokeWidth={9} fill="none" strokeLinecap="round" />
      <path d="M130 116 Q138 136 120 152" stroke={RUST} strokeWidth={9} fill="none" strokeLinecap="round" />
      {/* muzzle */}
      <ellipse cx="100" cy="138" rx="19" ry="15" fill="#FFFDF8" />
      <ellipse cx="100" cy="132" rx="7" ry="5.5" fill={NOSE} />
      {/* mouth */}
      {proud ? (
        <path d="M86 142 Q100 156 114 142" stroke={NOSE} strokeWidth={3} fill="none" strokeLinecap="round" />
      ) : hungry ? (
        <ellipse cx="100" cy="147" rx="6" ry="7" fill="#8A2B3A" />
      ) : (
        <path d="M92 143 Q100 150 108 143" stroke={NOSE} strokeWidth={3} fill="none" strokeLinecap="round" />
      )}
      {/* cheeks */}
      {(proud || mood === "Cheering") && (
        <>
          <circle cx="66" cy="132" r="7" fill={palette.orange} opacity={0.4} />
          <circle cx="134" cy="132" r="7" fill={palette.orange} opacity={0.4} />
        </>
      )}
      {/* ---- cosmetics ---- */}
      {cosmetic === "chef_hat" && (
        <g>
          <rect x="70" y="26" width="60" height="16" rx="6" fill="#fff" />
          <circle cx="76" cy="22" r="14" fill="#fff" />
          <circle cx="100" cy="16" r="16" fill="#fff" />
          <circle cx="124" cy="22" r="14" fill="#fff" />
        </g>
      )}
      {cosmetic === "sporty" && <rect x="44" y="70" width="112" height="14" rx="7" fill={palette.blue} />}
      {cosmetic === "sunglasses" && (
        <g>
          <rect x="56" y="98" width="38" height="20" rx="8" fill="#0A0E14" />
          <rect x="106" y="98" width="38" height="20" rx="8" fill="#0A0E14" />
          <rect x="94" y="104" width="12" height="5" fill="#0A0E14" />
        </g>
      )}
      {cosmetic === "wizard" && <polygon points="100,2 78,48 122,48" fill={palette.violet} />}
      {cosmetic === "astronaut" && <circle cx="100" cy="105" r="74" fill="none" stroke={palette.cyan} strokeWidth={5} opacity={0.6} />}
    </svg>
  );
}

export function Ring({ value, target, color, label, unit = "g", size = 110 }) {
  const stroke = 10;
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const pct = target > 0 ? Math.min(1, value / target) : 0;
  const remaining = Math.max(0, Math.round(target - value));
  return (
    <div className="flex flex-col items-center">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size}>
          <circle cx={size / 2} cy={size / 2} r={r} stroke="#EAE1D2" strokeWidth={stroke} fill="none" />
          <circle cx={size / 2} cy={size / 2} r={r} stroke={color} strokeWidth={stroke} fill="none"
            strokeDasharray={`${circ} ${circ}`} strokeDashoffset={circ * (1 - pct)}
            strokeLinecap="round" transform={`rotate(-90 ${size / 2} ${size / 2})`}
            style={{ transition: "stroke-dashoffset 0.6s ease" }} />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-xl font-extrabold text-slate-900">{Math.round(value)}</span>
          <span className="text-[10px] text-slate-500">/{target}{unit}</span>
        </div>
      </div>
      <span className="mt-2 text-sm font-bold" style={{ color }}>{label}</span>
      <span className="text-[11px] text-slate-500">{remaining}{unit} left</span>
    </div>
  );
}
